// TODO add link to Swagger?

import { requestApi, doAPIRequest } from '~/services/api-new';
import {
	CooloffResponse,
	DurationOptionsResponse,
	DurationUpdateResponse,
	GetDateResponse,
	RealityCheckNotificationInfoResponse,
	RealityCheckNotificationResponse
} from './types';
import {
	retryableErrorCodes,
	noActiveDurationErrorCode
} from '~/components/account/reality-check-new/constants/constants';

const maxRetries = 3;
const DELAY_BETWEEN_RETRIES = 500; // milliseconds
const delay = (ms: number) =>
	new Promise(resolve => {
		setTimeout(resolve, ms);
	});

export const viewRCNotification = async (notificationId: number) => {
	let attempt = 1;
	while (attempt <= maxRetries) {
		// eslint-disable-next-line no-await-in-loop
		const response = (await requestApi({
			endpoint: `/reality-check/notifications/${notificationId}/view`,
			method: 'PUT'
		})) as RealityCheckNotificationResponse;
		if (!('code' in response) || !retryableErrorCodes.includes(response.code as number)) {
			return response;
		}

		// eslint-disable-next-line no-await-in-loop
		await delay(DELAY_BETWEEN_RETRIES);
		attempt += 1;
	}
	throw new Error('Failed to view notification');
};

export const acknowledgeRCNotification = async (notificationId: number) => {
	let attempt = 1;
	while (attempt <= maxRetries) {
		// eslint-disable-next-line no-await-in-loop
		const response = (await requestApi({
			endpoint: `/reality-check/notifications/${notificationId}/acknowledgement`,
			method: 'PUT'
		})) as RealityCheckNotificationResponse;
		if (!('code' in response)) {
			return response;
		}

		// eslint-disable-next-line no-await-in-loop
		await delay(DELAY_BETWEEN_RETRIES);
		attempt += 1;
	}
	throw new Error('Failed to view notification');
};

export const getRCDurationOptions = async () => {
	const response = (await requestApi({
		endpoint: `/reality-check/duration-options`,
		method: 'GET'
	})) as DurationOptionsResponse;
	if ('code' in response) {
		throw new Error('Failed to get RC duration options');
	}
	return response;
};

export const getRCDuration = async () => {
	const response = (await requestApi({
		endpoint: `/reality-check/duration`,
		method: 'GET'
	})) as DurationUpdateResponse;
	if ('code' in response && response.code !== noActiveDurationErrorCode) {
		throw new Error('Failed to retrieve RC duration');
	}
	return response;
};

export const setRCDuration = async (durationInMinutes: number) => {
	const response = await doAPIRequest({
		endpoint: `/reality-check/duration`,
		method: 'PUT',
		payload: { durationInMinutes }
	});
	if ('code' in response) {
		throw new Error('Failed to set RC duration');
	}
	return response;
};

export const deleteRCDuration = async () => {
	const response = await doAPIRequest({
		endpoint: `/reality-check/duration`,
		method: 'DELETE'
	});
	if ('code' in response) {
		throw new Error('Failed to delete RC duration');
	}
	return response;
};

export const startRC = async () => {
	let attempt = 1;
	while (attempt <= maxRetries) {
		// eslint-disable-next-line no-await-in-loop
		const response = (await requestApi({
			endpoint: `/reality-check/start`,
			method: 'POST'
		})) as RealityCheckNotificationResponse;
		if (!('code' in response) || !retryableErrorCodes.includes(response.code as number)) {
			return response;
		}

		// eslint-disable-next-line no-await-in-loop
		await delay(DELAY_BETWEEN_RETRIES);
		attempt += 1;
	}
	throw new Error(`Failed to start RC after ${maxRetries} retries`);
};

export const getRealityCheckNotificationInfo = async (notificationId: number) => {
	const response = (await requestApi({
		endpoint: `/reality-check/notifications/${notificationId}/info`,
		method: 'GET'
	})) as RealityCheckNotificationInfoResponse;
	if ('code' in response) {
		throw new Error('Failed to retrieve RC notification info');
	}
	return response;
};

export const getNextRCNotification = async () => {
	let attempt = 1;
	while (attempt <= maxRetries) {
		// eslint-disable-next-line no-await-in-loop
		const response = (await requestApi({
			endpoint: `/reality-check/notifications/next`,
			method: 'GET'
		})) as RealityCheckNotificationResponse;

		// Handle the case where the response is 404, No notification available
		if ('code' in response && response.code === 2000) {
			return response;
		}

		// Return response and handles retryable errors
		if (!('code' in response) || !retryableErrorCodes.includes(response.code as number)) {
			return response;
		}

		// eslint-disable-next-line no-await-in-loop
		await delay(DELAY_BETWEEN_RETRIES);
		attempt += 1;
	}
	throw new Error(`Failed to retrieve RC notification after ${maxRetries} retries`);
};

export const getCurrentDate = async () => {
	return (await requestApi({
		endpoint: `/reality-check/date`,
		method: 'GET'
	})) as GetDateResponse;
};

export const getOngoingCoolOff = async () => {
	const response = (await requestApi({
		endpoint: `/reality-check/client/rc-cooloff`,
		method: 'GET'
	})) as CooloffResponse;
	if (typeof response === 'number') {
		return response * 1000; // Convert seconds to milliseconds
	}
	if ('error' in response && response?.error === 'Not Found') {
		return 0; // No ongoing cool-off period
	}
	throw new Error('Failed to retrieve ongoing cool-off data');
};
